import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Performer } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdatePerformer } from '@/hooks/usePerformers'
import { useToast } from '@/hooks/use-toast'
import { isFlagged, isEffectivelyMatched } from '@/lib/confidence'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink, WikidataSearchLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export type PerformerFilter =
  | 'all'
  | 'matched'
  | 'unmatched'
  | 'flagged'
  | 'verified'
  | 'needs_review'

interface PerformersTableProps {
  performers: Performer[]
  filter: PerformerFilter
  onFilterChange: (filter: PerformerFilter) => void
}

export function PerformersTable({ performers, filter, onFilterChange }: PerformersTableProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const updatePerformer = useUpdatePerformer()
  const { toast } = useToast()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'appearances', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const filteredPerformers = useMemo(
    () =>
      performers.filter((p) => {
        switch (filter) {
          case 'matched':
            return isEffectivelyMatched(p)
          case 'unmatched':
            return !isEffectivelyMatched(p)
          case 'flagged':
            return isFlagged(p.name_canonical, p.wikidata_label, p.confidence, p.quality_score)
          case 'verified':
            return p.quality_score === 'verified'
          case 'needs_review':
            return p.quality_score === 'needs_review'
          default:
            return true
        }
      }),
    [performers, filter],
  )

  const handleQualityChange = useCallback(
    async (performer: Performer, qualityScore: QualityScore) => {
      try {
        await updatePerformer.mutateAsync({
          id: performer.id,
          input: { quality_score: qualityScore },
        })
        toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update quality',
        })
      }
    },
    [updatePerformer, toast],
  )

  const handleQidSave = useCallback(
    async (performer: Performer, qid: string) => {
      try {
        const result = await updatePerformer.mutateAsync({
          id: performer.id,
          input: { wikidata_id: qid },
        })
        if (result.enriched) {
          toast({
            title: 'Enriched',
            description: `Matched to ${result.enriched.label}`,
          })
        } else if (result.cleared) {
          toast({ title: 'Cleared', description: 'Wikidata ID removed' })
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update QID',
        })
      }
    },
    [updatePerformer, toast],
  )

  const columns: ColumnDef<Performer>[] = useMemo(
    () => [
      {
        accessorKey: 'name_canonical',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <span className="font-medium">{row.getValue('name_canonical')}</span>,
      },
      {
        accessorKey: 'instrument_or_voice',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Instrument / Voice
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const value = row.getValue('instrument_or_voice') as string | null
          return value ? (
            <span className="text-sm">{value}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        },
      },
      {
        accessorKey: 'wikidata_id',
        header: 'QID',
        cell: ({ row }) => {
          const performer = row.original
          if (canEdit) {
            return (
              <InlineEdit
                value={performer.wikidata_id || ''}
                onSave={(qid) => handleQidSave(performer, qid)}
                placeholder="Add QID"
              />
            )
          }
          return <WikidataLink qid={performer.wikidata_id} />
        },
      },
      {
        accessorKey: 'wikidata_label',
        header: 'Wikidata Label',
        cell: ({ row }) => {
          const performer = row.original
          if (performer.wikidata_label) {
            return <WikidataLink qid={performer.wikidata_id} label={performer.wikidata_label} />
          }
          if (performer.name_canonical && !performer.wikidata_id) {
            return <WikidataSearchLink text={performer.name_canonical} />
          }
          return <span className="text-muted-foreground">-</span>
        },
      },
      {
        accessorKey: 'confidence',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Confidence
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <ConfidenceBadge
            confidence={row.original.confidence}
            qualityScore={row.original.quality_score}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.confidence ?? -1
          const b = rowB.original.confidence ?? -1
          return a - b
        },
      },
      {
        accessorKey: 'appearances',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Appearances
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <span className="tabular-nums">{row.getValue('appearances')}</span>,
      },
      {
        accessorKey: 'quality_score',
        header: 'Status',
        cell: ({ row }) => {
          const performer = row.original
          if (canEdit) {
            return (
              <QualityDropdown
                value={performer.quality_score}
                onChange={(qs) => handleQualityChange(performer, qs)}
                disabled={updatePerformer.isPending}
              />
            )
          }
          return (
            <span className="text-sm capitalize">{performer.quality_score.replace('_', ' ')}</span>
          )
        },
      },
    ],
    [canEdit, handleQidSave, handleQualityChange, updatePerformer.isPending],
  )

  const table = useReactTable({
    data: filteredPerformers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageSize: 25 },
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Search performers..."
          value={(table.getColumn('name_canonical')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('name_canonical')?.setFilterValue(e.target.value)}
          className="max-w-sm"
        />
        <Input
          placeholder="Filter by instrument..."
          value={(table.getColumn('instrument_or_voice')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('instrument_or_voice')?.setFilterValue(e.target.value)}
          className="max-w-[200px]"
        />
        <Select value={filter} onValueChange={(v) => onFilterChange(v as PerformerFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="unmatched">Unmatched</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of {filteredPerformers.length} performers
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/performers/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No performers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
